// -- APP STORE REQUIREMENTS (2026-10-30) ----------------------------------------------------
// AI consent screen (Apple 5.1.2(i)): shown by api() in App.jsx when an AI route answers
// 403 { needsAiConsent: true }; the member's answer is saved and the call is tried once more.
// Settings > My account: AI features on/off, blocked members (unblock), help and contact.
// Messages: "Report a problem with this conversation" (+ "Ask for a different trainer").
// Backend: backend/appstore-safety.js.
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';

// The consent screen is mounted once (AiConsentHost). askAiConsent() resolves true (Allow),
// false (Not now) or null (closed, or no screen mounted).
let openConsent = null;
export function askAiConsent() {
  return new Promise(resolve => { if (!openConsent) return resolve(null); openConsent(resolve); });
}

export default function makeAppStore({ T, Btn, Card, api, ui }) {
  const { H, Note, Err, fieldStyle, fmtDate } = ui;
  const Row = ({ children, style }) => <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', ...style }}>{children}</div>;

  function ConsentText({ t }) {
    return <>
      <p style={{ fontSize: 15, lineHeight: 1.55, marginTop: 0 }}>{t.intro}</p>
      <p style={{ fontSize: 14, fontWeight: 700, margin: '10px 0 4px' }}>What is sent</p>
      <ul style={{ fontSize: 14, lineHeight: 1.5, margin: '0 0 8px', paddingLeft: 20 }}>{t.sent.map(s => <li key={s}>{s}</li>)}</ul>
      <p style={{ fontSize: 14, fontWeight: 700, margin: '10px 0 4px' }}>Not used to train AI models</p>
      <p style={{ fontSize: 14, margin: 0 }}>{t.training}</p>
      <p style={{ fontSize: 12, color: T.gray, margin: '2px 0 8px' }}>Source: {t.trainingSource}</p>
      <p style={{ fontSize: 14, margin: '8px 0' }}>{t.without}</p>
      <p style={{ fontSize: 14, margin: '8px 0' }}><b>{t.change}</b></p>
      <p style={{ fontSize: 13, color: T.gray, margin: '8px 0 0' }}>{t.minor}</p>
    </>;
  }

  function AiConsentHost() {
    const [pending, setPending] = useState(null), [d, setD] = useState(null), [err, setErr] = useState('');
    useEffect(() => {
      openConsent = resolve => { setErr(''); setD(null); setPending(() => resolve); api('/api/ai-consent').then(setD).catch(e => setErr(e.message)); };
      return () => { openConsent = null; };
    }, []);
    if (!pending) return null;
    const answer = v => { const r = pending; setPending(null); r(v); };
    return (
      <div role="dialog" aria-modal="true" aria-labelledby="ai-consent-title" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
        <Card style={{ maxWidth: 560, width: '100%', maxHeight: '90vh', overflowY: 'auto', padding: 22, background: T.card || '#fff' }}>
          <h2 id="ai-consent-title" style={{ fontFamily: 'Oswald', fontSize: 22, margin: '0 0 12px' }}>{d ? d.text.title : 'Use AI features?'}</h2>
          {d ? <ConsentText t={d.text} /> : <><Err msg={err} />{!err && <p style={{ color: T.gray }}>Loading...</p>}</>}
          <Row style={{ marginTop: 16 }}>
            <Btn onClick={() => answer(true)} disabled={!d}>Allow</Btn>
            <Btn variant="ghost" onClick={() => answer(false)} disabled={!d}>Not now</Btn>
          </Row>
        </Card>
      </div>
    );
  }

  // ---- Settings > My account ---------------------------------------------------------------------
  function AiSettingsCard() {
    const [d, setD] = useState(null), [err, setErr] = useState(''), [ok, setOk] = useState('');
    const load = useCallback(() => api('/api/ai-consent').then(setD).catch(e => setErr(e.message)), []);
    useEffect(() => { load(); }, [load]);
    const on = d && d.status === 'granted';
    const flip = async () => {
      setErr(''); setOk('');
      try {
        if (on) { await api('/api/ai-consent', 'POST', { allow: false }); setOk('AI features are off. Nothing new about you is sent to the AI.'); }
        else {
          const v = await askAiConsent();
          if (v === null) return;
          await api('/api/ai-consent', 'POST', { allow: v });
          setOk(v ? 'AI features are on.' : 'AI features stay off.');
        }
      } catch (e) { setErr(e.message); }
      load();
    };
    return (
      <Card style={{ padding: 18, marginBottom: 12 }}>
        <H>AI features</H>
        {!d ? <Err msg={err} /> : <>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 14 }}>
            <input type="checkbox" checked={on} onChange={flip} aria-label="Use AI features" style={{ width: 18, height: 18 }} />
            <span><b>Use AI features</b> (Anthropic's Claude): Snap a Meal, Say it, the AI coach and Help answers</span>
          </label>
          <p style={{ fontSize: 13, color: T.gray, margin: '6px 0 0' }}>
            {on ? `On since ${fmtDate(d.decided_at)}.` : d.status === 'declined' ? `Off (you chose "Not now" on ${fmtDate(d.decided_at)}).` : d.outdated ? 'Off until you agree to the updated wording.' : 'You have not chosen yet. We ask the first time you use an AI feature.'}
            {d.minor && !d.guardianConsent ? ' Members under 18 need a parent or guardian\'s consent on file first.' : ''}
          </p>
          <details style={{ marginTop: 8 }}><summary style={{ cursor: 'pointer', fontSize: 14, color: T.orange, fontWeight: 600 }}>What is sent, and to whom</summary><div style={{ marginTop: 8 }}><ConsentText t={d.text} /></div></details>
          <Err msg={err} />{ok && <Note color={T.success}>{ok}</Note>}
        </>}
      </Card>
    );
  }

  function BlockedCard() {
    const [d, setD] = useState(null), [err, setErr] = useState('');
    const load = useCallback(() => api('/api/blocks').then(r => setD(r.blocked)).catch(e => setErr(e.message)), []);
    useEffect(() => { load(); }, [load]);
    const unblock = async id => { setErr(''); try { await api(`/api/blocks/${id}`, 'DELETE'); load(); } catch (e) { setErr(e.message); } };
    return (
      <Card style={{ padding: 18, marginBottom: 12 }}>
        <H>Blocked members</H>
        {!d ? <Err msg={err} /> : !d.length ? <p style={{ fontSize: 14, color: T.gray, margin: 0 }}>You have not blocked anyone. To block someone, tap "Block" on one of their group posts. They are not told.</p>
          : d.map(b => <Row key={b.id} style={{ justifyContent: 'space-between', padding: '6px 0', borderBottom: `1px solid ${T.border}` }}>
            <span style={{ fontSize: 14 }}>{b.name} <span style={{ color: T.gray, fontSize: 12 }}>since {fmtDate(b.since)}</span></span>
            <Btn size="sm" variant="ghost" onClick={() => unblock(b.id)}>Unblock {b.name}</Btn>
          </Row>)}
        <Err msg={d ? err : ''} />
      </Card>
    );
  }

  function ContactCard() {
    const [c, setC] = useState(null);
    useEffect(() => { api('/api/support/contact').then(setC).catch(() => {}); }, []);
    const email = (c && c.email) || 'support@healthybody.fitness';
    return (
      <Card style={{ padding: 18, marginBottom: 12 }}>
        <H>Help and contact</H>
        <p style={{ fontSize: 14, margin: '0 0 6px' }}>Questions, problems or a report about another member or a trainer: <a href={`mailto:${email}`} style={{ color: T.orange, fontWeight: 600 }}>{email}</a>, or <Link to="/help" style={{ color: T.orange, fontWeight: 600 }}>Help</Link> in the app.</p>
        <p style={{ fontSize: 14, margin: 0 }}>A person reviews every report within 24 hours. Report a group post with "Report" under it, or your trainer conversation with "Report a problem with this conversation" in <Link to="/messages" style={{ color: T.orange }}>Messages</Link>.</p>
      </Card>
    );
  }

  function AccountExtras() {
    return <><AiSettingsCard /><BlockedCard /><ContactCard /></>;
  }

  // ---- Messages: report the conversation ------------------------------------------------------------
  function ChatReport() {
    const [open, setOpen] = useState(false), [note, setNote] = useState(''), [other, setOther] = useState(false), [err, setErr] = useState(''), [done, setDone] = useState(''), [busy, setBusy] = useState(false);
    const send = async () => {
      setErr(''); setBusy(true);
      try { const r = await api('/api/chat/report', 'POST', { note, differentTrainer: other }); setDone(r.message); setOpen(false); setNote(''); setOther(false); } catch (e) { setErr(e.message); }
      setBusy(false);
    };
    return (
      <div style={{ marginTop: 10 }}>
        {done && <Note color={T.success}>{done}</Note>}
        {!open ? <button type="button" onClick={() => { setOpen(true); setDone(''); }} style={{ background: 'none', border: 'none', padding: 0, color: T.gray, fontSize: 13, textDecoration: 'underline', cursor: 'pointer' }}>Report a problem with this conversation</button>
          : <Card style={{ padding: 14, marginTop: 6 }}>
            <p style={{ fontSize: 14, marginTop: 0 }}>This sends your recent messages in this conversation to the HealthyBody team (not your trainer). A person reviews it within 24 hours. In an emergency, call 911.</p>
            <textarea rows={3} value={note} onChange={e => setNote(e.target.value)} placeholder="What went wrong?" aria-label="What went wrong" style={fieldStyle} />
            <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 14, margin: '8px 0' }}><input type="checkbox" checked={other} onChange={() => setOther(!other)} style={{ width: 18, height: 18 }} />Ask for a different trainer</label>
            <Err msg={err} />
            <Row><Btn size="sm" onClick={send} disabled={busy || (!note.trim() && !other)}>{busy ? 'Sending...' : 'Send report'}</Btn><Btn size="sm" variant="ghost" onClick={() => setOpen(false)}>Cancel</Btn></Row>
          </Card>}
      </div>
    );
  }

  // ---- Admin: the report behind a Support ticket ----------------------------------------------------
  function ReportExcerpt({ reports }) {
    if (!reports || !reports.length) return null;
    return reports.map(r => (
      <Note key={r.id} color={T.danger}>
        <b>{r.kind === 'chat' ? 'Reported trainer conversation' : 'Reported group post'}</b>{r.differentTrainer ? ' - asked for a different trainer (reassign in Rebuilder care & assignment)' : ''}. Act by {fmtDate(r.dueBy)} (24 hours).
        {r.note ? <p style={{ margin: '6px 0' }}>Member wrote: <i>{r.note}</i></p> : null}
        <ul style={{ fontSize: 13, margin: '6px 0 0', paddingLeft: 18 }}>{r.excerpt.map((m, i) => <li key={i}><span style={{ color: T.gray }}>{m.when}</span> <b>{m.who}</b>: {m.text}</li>)}</ul>
      </Note>
    ));
  }

  return { AiConsentHost, AccountExtras, AiSettingsCard, BlockedCard, ContactCard, ChatReport, ReportExcerpt };
}
